import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  NavigationContainer,
  NavigationItem,
  NavigationItemWrapper,
  NavigationDivider,
  NavigationText,
  NavigationIcon,
} from './ApplicationNavigation.styles';

import { ArrowUpward } from '@mui/icons-material';

export interface NavigationItemData {
  label: string;
  id?: string;
  url?: string;
}

const defaultNavigationItems: NavigationItemData[] = [
  { id: 'key-challenges', label: 'Key Challenges' },
  { id: 'key-capabilities', label: 'Key Capabilities' },
  { id: 'why-choose-us', label: 'Why Teams Choose AiLS' },
];

const scrollToSection = (id: string, offset = -30) => {
  const element = document.getElementById(id);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

interface ApplicationNavigationProps {
  items?: NavigationItemData[];
  maxWidth?: number | string;
  equalItems?: boolean;
  scrollOffset?: number;
  margin?: string;
  noTruncate?: boolean;
  fontSize?: number | string;
}

export const ApplicationNavigation: FC<ApplicationNavigationProps> = ({
  items = defaultNavigationItems,
  maxWidth,
  equalItems = false,
  scrollOffset,
  margin,
  noTruncate = false,
  fontSize,
}) => {
  const navigate = useNavigate();

  const handleClick = (item: NavigationItemData) => {
    if (item.url) {
      navigate(item.url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (item.id) {
      scrollToSection(item.id, scrollOffset);
    }
  };

  const textSx = {
    ...(noTruncate
      ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip' }
      : {}),
    ...(fontSize ? { fontSize } : {}),
  };

  const containerSx = {
    ...(maxWidth ? { maxWidth } : {}),
    ...(margin ? { margin } : {}),
    ...(equalItems ? { '& > *:last-child': { flex: '1 1 0 !important' } } : {}),
    ...(noTruncate ? { height: 'auto' } : {}),
    ...(Object.keys(textSx).length ? { '& p': textSx } : {}),
  };

  return (
    <NavigationContainer sx={containerSx}>
      {items.map((item, index) => (
        <NavigationItemWrapper key={item.label}>
          {index === 0 && <NavigationDivider />}
          {index > 0 && <NavigationDivider />}
          <NavigationItem onClick={() => handleClick(item)}>
            <NavigationText>{item.label}</NavigationText>
            <NavigationIcon>
              <ArrowUpward />
            </NavigationIcon>
          </NavigationItem>
          {index === items.length - 1 && <NavigationDivider />}
        </NavigationItemWrapper>
      ))}
    </NavigationContainer>
  );
};
